import React from "react";
import type { CustomModalOptions } from "../../../notifications/types";
import { ExportDatasetSnippetContent } from "./ExportDatasetSnippetContent";

type ShowModalFn = (options: CustomModalOptions) => Promise<void>;

const STORAGE_KEY = "rf:exportDataset:codeFormats";

/** Download URLs expire after 7 days, so stored formats older than that are useless. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredEntry {
    format: string;
    datasetType?: string;
    ts: number;
}

/** In-memory cache for current session. localStorage is the reload-surviving fallback. */
const codeExportDatasetFormats = new Map<string, StoredEntry>();

/** Removes localStorage entries older than {@link TTL_MS}. */
function pruneStaleEntries(stored: Record<string, StoredEntry>): Record<string, StoredEntry> {
    const now = Date.now();
    const pruned: Record<string, StoredEntry> = {};
    for (const [id, entry] of Object.entries(stored)) {
        if (now - entry.ts < TTL_MS) pruned[id] = entry;
    }
    return pruned;
}

/** Called at dispatch time by useExportDatasetModal when the user picks code snippet mode. */
function registerCodeExportDataset(taskId: string, format: string, datasetType?: string): void {
    const entry: StoredEntry = { format, datasetType, ts: Date.now() };
    codeExportDatasetFormats.set(taskId, entry);
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        const pruned = pruneStaleEntries(stored);
        pruned[taskId] = entry;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
    } catch {
        // localStorage unavailable
    }
}

function getCodeExportDatasetFormat(taskId: string): StoredEntry | null {
    const memoryHit = codeExportDatasetFormats.get(taskId);
    if (memoryHit) return memoryHit;
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        const entry = stored[taskId];
        if (!entry?.format) return null;
        if (Date.now() - (entry.ts || 0) >= TTL_MS) return null;
        return entry;
    } catch {
        return null;
    }
}

/**
 * Returns the completion action for an exportDataset task.
 *
 * - Code snippet exports → "Get snippet" → opens CodeSnippetView via notification modal
 * - ZIP exports (or reload fallback) → "Download" → window.open
 *
 * `showModal` is injected by the caller (ultimately from `useNotification()`)
 * so this module stays hook-agnostic and avoids reaching for `window.rfNotify`.
 */
function getExportDatasetAction(
    taskId: string,
    downloadUrl: string,
    params: { workspaceUrl?: string; name?: string } | null,
    showModal: ShowModalFn
): { label: string; onClick: () => void } {
    const entry = getCodeExportDatasetFormat(taskId);
    if (entry) {
        return {
            label: "Get snippet",
            onClick: () => {
                const options: CustomModalOptions = {
                    icon: "far fa-download",
                    title: "Export Dataset",
                    size: "lg",
                    submitText: "Done",
                    render: (close) => (
                        <ExportDatasetSnippetContent
                            downloadUrl={downloadUrl}
                            format={entry.format}
                            datasetType={entry.datasetType}
                            workspaceUrl={params?.workspaceUrl || ""}
                            datasetName={params?.name || ""}
                            onClose={close}
                        />
                    )
                };
                void showModal(options);
            }
        };
    }

    return {
        label: "Download",
        onClick: () => {
            window.open?.(downloadUrl, "_blank");
        }
    };
}

export { registerCodeExportDataset, getExportDatasetAction };
